import React from 'react';
import { Button, Field } from '@odin-labs/components';
import { TrashAltIcon } from 'components/icons';
import { JobsiteFormSubmissionVisitor } from './types';

export type FormSubmissionEditableVisitorProps = {
  visitor: JobsiteFormSubmissionVisitor;
  updateVisitor: (
    visitorChanges: Partial<JobsiteFormSubmissionVisitor>,
  ) => void;
  removeVisitor: (visitorId: number) => void;
  error: Record<keyof JobsiteFormSubmissionVisitor, string>;
};

export const FormSubmissionEditableVisitor = React.forwardRef<
  HTMLDivElement & { focusFirstError: () => void },
  FormSubmissionEditableVisitorProps
>((props, ref): React.ReactElement => {
  const { visitor, updateVisitor, removeVisitor, error } = props;
  const { id: visitorId, name, company, reasonForVisit } = visitor ?? {};

  const updateState = React.useCallback(
    (visitorChanges: Partial<JobsiteFormSubmissionVisitor>): void =>
      updateVisitor({
        id: visitorId,
        ...visitorChanges,
      }),
    [updateVisitor, visitorId],
  );

  const nameRef = React.useRef<HTMLInputElement>();
  const companyRef = React.useRef<HTMLInputElement>();
  const reasonForVisitRef = React.useRef<HTMLInputElement>();

  const innerRef = React.useRef<HTMLDivElement>();
  React.useImperativeHandle(ref, () => {
    return (
      innerRef.current &&
      Object.assign(innerRef.current, {
        focusFirstError() {
          if (error.name) {
            nameRef.current?.focus();
          } else if (error.company) {
            companyRef.current?.focus();
          } else if (error.reasonForVisit) {
            reasonForVisitRef.current?.focus();
          }
        },
      })
    );
  });

  return (
    <div
      ref={innerRef}
      className={`odin-gap-3 odin-grid odin-grid-cols-4 odin-p-5
      odin-border odin-border-gray-200 odin-shadow-sm odin-rounded-lg`}
    >
      <div className="odin-col-span-4 odin-flex odin-justify-end">
        <Button
          theme="danger"
          size="sm"
          icon={TrashAltIcon}
          onClick={() => removeVisitor(visitorId)}
        />
      </div>
      <div className="odin-col-span-4 sm:odin-col-span-2">
        <Field
          ref={nameRef}
          name={`name-${visitorId}`}
          label="Name"
          value={name ?? ''}
          onChange={(changedValue): void =>
            updateState({ name: changedValue || null })
          }
          error={error?.name}
        />
      </div>
      <div className="odin-col-span-4 sm:odin-col-span-2">
        <Field
          ref={companyRef}
          name={`company-${visitorId}`}
          label="Company"
          value={company ?? ''}
          onChange={(changedValue): void =>
            updateState({ company: changedValue || null })
          }
          error={error?.company}
        />
      </div>
      <div className="odin-col-span-4">
        <Field
          ref={reasonForVisitRef}
          name={`reasonForVisit-${visitorId}`}
          label="Reason for Visit"
          value={reasonForVisit ?? ''}
          onChange={(changedValue): void =>
            updateState({ reasonForVisit: changedValue || null })
          }
          error={error?.reasonForVisit}
        />
      </div>
    </div>
  );
});
